import { Component } from './component.js';

/**
 * `<reviews-carousel-component>` — paged review carousel with arrows, dots, and autoplay.
 *
 * Built on the theme's `Component` base class (not a page-level `DOMContentLoaded`
 * listener) so it re-initializes correctly via `connectedCallback`/`updatedCallback`
 * when Shopify's Section Rendering API morphs this section in the theme editor —
 * a plain `DOMContentLoaded` listener would only ever run once and silently stop
 * working after any live settings change.
 *
 * @typedef {{track: HTMLElement, dots: HTMLElement, prevArrow?: HTMLElement, nextArrow?: HTMLElement}} Refs
 * @extends {Component<Refs>}
 */
class ReviewsCarousel extends Component {
  requiredRefs = ['track', 'dots'];

  /** @type {number} */
  #current = 0;

  /** @type {number | null} */
  #timer = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {HTMLButtonElement[]} */
  #dotEls = [];

  connectedCallback() {
    super.connectedCallback();
    this.#build();

    this.#resizeObserver = new ResizeObserver(() => this.#build());
    this.#resizeObserver.observe(this);

    this.addEventListener('pointerenter', this.#stopAutoplay);
    this.addEventListener('pointerleave', this.#startAutoplay);

    this.#startAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAutoplay();
    this.#resizeObserver?.disconnect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#build();
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return Array.from(this.refs.track?.children ?? /** @type {HTMLElement[]} */ ([]));
  }

  #slidesPerView() {
    const width = window.innerWidth;
    if (width >= 1000) return 3;
    if (width >= 700) return 2;
    return 1;
  }

  #build() {
    const slides = this.#slides;
    if (slides.length === 0) return;

    const perView = this.#slidesPerView();
    const pageCount = Math.max(1, Math.ceil(slides.length / perView));
    this.#current = Math.min(this.#current, pageCount - 1);
    this.#perView = perView;
    this.#pageCount = pageCount;
    this.#buildDots(pageCount);
    this.#updateArrows();
  }

  /** @type {number} */
  #perView = 1;
  /** @type {number} */
  #pageCount = 1;

  /** @param {number} pageCount */
  #buildDots(pageCount) {
    const dotsWrap = this.refs.dots;
    if (!dotsWrap) return;

    dotsWrap.innerHTML = '';
    this.#dotEls = [];

    for (let i = 0; i < pageCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'reviews-carousel__dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => {
        this.goTo(i);
        this.#restartAutoplay();
      });
      dotsWrap.appendChild(dot);
      this.#dotEls.push(dot);
    }

    this.#updateDots();
  }

  #updateDots() {
    this.#dotEls.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.#current);
    });
  }

  #updateArrows() {
    const { prevArrow, nextArrow } = this.refs;
    const loopable = this.#pageCount > 1;
    if (prevArrow instanceof HTMLButtonElement) prevArrow.disabled = !loopable;
    if (nextArrow instanceof HTMLButtonElement) nextArrow.disabled = !loopable;
  }

  /** @param {number} index */
  goTo(index) {
    const { track } = this.refs;
    const slides = this.#slides;
    if (!track || slides.length === 0) return;

    this.#current = (index + this.#pageCount) % this.#pageCount;

    const slideRect = slides[0].getBoundingClientRect();
    const trackStyle = getComputedStyle(track);
    const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || '0') || 0;
    const offset = this.#current * this.#perView * (slideRect.width + gap);

    track.scrollTo({ left: offset, behavior: 'smooth' });
    this.#updateDots();
  }

  next() {
    this.goTo(this.#current + 1);
    this.#restartAutoplay();
  }

  previous() {
    this.goTo(this.#current - 1);
    this.#restartAutoplay();
  }

  #startAutoplay = () => {
    if (this.dataset.autoplay !== 'true') return;
    const interval = parseInt(this.dataset.interval ?? '', 10) || 5000;
    this.#stopAutoplay();
    this.#timer = window.setInterval(() => this.goTo(this.#current + 1), interval);
  };

  #stopAutoplay = () => {
    if (this.#timer !== null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  };

  #restartAutoplay() {
    if (this.dataset.autoplay !== 'true') return;
    this.#startAutoplay();
  }
}

if (!customElements.get('reviews-carousel-component')) {
  customElements.define('reviews-carousel-component', ReviewsCarousel);
}
